import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MenuModule } from 'primeng/menu';
import { TabViewModule } from 'primeng/tabview';
import { NotificationService } from '../../services/notification.service';
import { Notification, NotificationParams } from '../../models/notification';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AppIcons } from '../../../modules/shared/app.icons';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-notification-tray',
  templateUrl: './notification-tray.component.html',
  styleUrls: ['./notification-tray.component.scss'],
  standalone: true,
  imports: [MenuModule, CommonModule, TabViewModule, ButtonModule, AvatarModule, CardModule, TranslateModule]
})
export class NotificationTrayComponent implements OnInit {
  @Input() notifications: Notification[] = [];
  @Input() unReadNotificationsCount: number = 0;
  @Output() markAllRead = new EventEmitter<void>();
  @Output() actionClicked = new EventEmitter<{ notificationId: string, action: string }>();
  @Output() loadMore = new EventEmitter<void>();
  @Input() notificationParams: NotificationParams;
  @Input() notificationCount: number = 0;
  miIcons = AppIcons;

  activeTab: 'all' | 'archived' = 'all';

  private notificationService = inject(NotificationService);
  private sanitizer = inject(DomSanitizer);
  isVisible = this.notificationService.isNotificationTrayOpen$;

  readonly notificationIcons = {
    dashboard: AppIcons.DASHBOARD,
    change_review: AppIcons.CHANGE_REVIEW,
    protocol_product: AppIcons.PRODUCT,
    protocol_sponsor: AppIcons.SPONSOR,
    protocol_user: AppIcons.USER_SIDEBAR,
    protocol_patient: AppIcons.PATIENT,
    protocol_order: AppIcons.ORDER,
    protducts: AppIcons.PRODUCT,
    site: AppIcons.SITE,
    order: AppIcons.ORDER,
    protocol: AppIcons.TRIAL,
    dispatch: AppIcons.USERS,
    report: AppIcons.REPORT,
    order_reports: AppIcons.REPORT,
    patient: AppIcons.PATIENT,
    sponsor: AppIcons.SPONSOR,
    users_and_roles: AppIcons.USER_SIDEBAR,
    audit_logs: AppIcons.AUDIT_LOGS,
    invoices: AppIcons.INVOICE_ICON,
    concerns: AppIcons.CONCERNS,
    shift_management: AppIcons.CONCERNS,
  };

  constructor(private router: Router) { }

  ngOnInit() {
  }

  getNotificationIcon(extraParam: string): string {
    const extraParamData = typeof extraParam === 'string' ? JSON.parse(extraParam) : extraParam;
    const code = extraParamData['entity-code'] || extraParamData.entityCode;
    return this.notificationIcons[code] || 'pi pi-info-circle';
  }

  onActionClick(notificationId: string, action: string): void {
    this.actionClicked.emit({ notificationId, action });
  }

  onMarkAllRead(action?: string): void {
    this.notificationService.markAllAsRead(action).subscribe({
      next: () => {
        this.markAllRead.emit();
      },
      error: () => {
        // Handle error marking notifications as read
      }
    });
  }

  close(): void {
    this.notificationService.closeNotificationTray();
  }


  getTimeAgo(date: string | Date): string {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
  }

  onLoadMore(): void {
    this.loadMore.emit();
  }

  formatTime(date: string | Date): string {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  getNotificationDescription(notification: Notification): string {
    return notification.description || '';
  }

  menuItems: MenuItem[] = [
    {
      label: 'Clear all notifications',
      icon: 'pi pi-trash',
      command: () => this.onMarkAllRead('CLEAR_ALL')
    },
    {
      label: 'Mark all as read',
      icon: 'pi pi-check',
      command: () => this.onMarkAllRead()
    }
  ];


  markAllAsRead() {

  }

  getNotificationMsgHtml(notification: Notification): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(notification.notificationMsg || '');
  }

  onNotificationClick(notification: any): void {
    let extraParam: any = {};
    if (notification.extraParam) {
      try {
        extraParam = typeof notification.extraParam === 'string' ? JSON.parse(notification.extraParam) : notification.extraParam;
      } catch (e) {
        extraParam = {};
      }
    }
    const entityId = extraParam.entityId;
    const entityCode = extraParam['entity-code'] || extraParam.entityCode;
    const parentId = extraParam.parentId;
    const parentCode = extraParam['parent-code'] || extraParam.parentCode;

    if (parentCode === 'protocol') {
      let tab = 'overview';

      if (entityCode === 'protocol_product') {
        tab = 'products';
      } else if (entityCode === 'protocol_order') {
        tab = 'orders';
      } else if (entityCode === 'protocol_sponsor') {
        tab = 'sponsors';
      } else if (entityCode === 'protocol_user') {
        tab = 'protocol-users';
      } else if (entityCode === 'protocol_patient') {
        tab = 'patients';
      } else if (entityCode === 'protocol_site') {
        tab = 'sites';
      }

      this.router.navigate(['app/trials/trial-details'], {
        queryParams: {
          tab,
          id: parentId
        }
      });
    }

    if (entityCode === 'order') {
      this.router.navigate([`app/orders/order-details`], {
        queryParams: {
          id: entityId
        }
      });
    }

    if (entityCode === 'site') {
      this.router.navigate([`app/sites/site-details`], {
        queryParams: {
          id: entityId
        }
      });
    }

    if (entityCode === 'dispatch') {
      this.router.navigate([`app/dispatch/dispatch-details`], {
        queryParams: {
          id: entityId
        }
      });
    }

    if (entityCode === 'protocol') {
      this.router.navigate([`app/trials/trial-details`], {
        queryParams: {
          id: entityId
        }
      });
    }

    if (entityCode === 'user') {
      this.router.navigate([`app/manage-users/user-details`], {
        queryParams: {
          id: entityId
        }
      });
    }


  }

} 